import liff from '@line/liff';
import { LiffProfile, LiffData, IdToken } from '../types/liff';
import { mockProfile, isDevelopmentMode } from './mockData';

const LIFF_ID = (import.meta as any).env?.VITE_LIFF_ID;

export class LiffManager {
  private static instance: LiffManager;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): LiffManager {
    if (!LiffManager.instance) {
      LiffManager.instance = new LiffManager();
    }
    return LiffManager.instance;
  }

  async init(): Promise<void> {
    if (this.isInitialized) return;

    // 開発モードの場合はLIFF初期化をスキップ
    if (isDevelopmentMode()) {
      console.log('Development mode: Skipping LIFF initialization');
      this.isInitialized = true;
      return;
    }

    if (!LIFF_ID || LIFF_ID.includes('dummy')) {
      throw new Error('LIFF_ID is not configured. Please set VITE_LIFF_ID in environment variables.');
    }

    try {
      console.log('Starting LIFF init with ID:', LIFF_ID);
      await liff.init({ liffId: LIFF_ID });
      this.isInitialized = true;
      console.log('LIFF initialization successful');
    } catch (error: any) {
      console.error('LIFF initialization failed:', error);
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        liffId: LIFF_ID,
        url: window.location.href,
        origin: window.location.origin
      });
      throw error;
    }
  }

  isLoggedIn(): boolean {
    if (isDevelopmentMode()) {
      return true; // 開発モードでは常にログイン済み扱い
    }
    return liff.isLoggedIn();
  }

  async login(): Promise<void> {
    if (!this.isInitialized) {
      await this.init();
    }

    if (!this.isLoggedIn()) {
      liff.login();
    }
  }

  logout(): void {
    liff.logout();
  }

  async getProfile(): Promise<LiffProfile> {
    if (!this.isLoggedIn()) {
      throw new Error('User is not logged in');
    }

    // 開発モードの場合はモックプロフィールを返す
    if (isDevelopmentMode()) {
      return mockProfile;
    }

    try {
      const profile = await liff.getProfile();
      return {
        userId: profile.userId,
        displayName: profile.displayName,
        pictureUrl: profile.pictureUrl,
        statusMessage: profile.statusMessage,
      };
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw error;
    }
  }

  async getIdToken(): Promise<string> {
    if (!this.isLoggedIn()) {
      throw new Error('User is not logged in');
    }

    // 開発モードの場合はダミートークンを返す
    if (isDevelopmentMode()) {
      return 'mock-id-token';
    }

    try {
      return liff.getIDToken() || '';
    } catch (error) {
      console.error('Failed to get ID token:', error);
      throw error;
    }
  }

  checkInitialized(): boolean {
    return this.isInitialized;
  }

  getOS(): string {
    return (liff as any).getOS ? (liff as any).getOS() : 'unknown';
  }

  getLanguage(): string {
    return (liff as any).getLanguage ? (liff as any).getLanguage() : 'unknown';
  }

  getVersion(): string {
    return (liff as any).getVersion ? (liff as any).getVersion() : 'unknown';
  }

  getLineVersion(): string | null {
    return (liff as any).getLineVersion ? (liff as any).getLineVersion() : null;
  }

  getContext(): any {
    return (liff as any).getContext ? (liff as any).getContext() : null;
  }

  async getDecodedIdToken(): Promise<IdToken> {
    if (!this.isLoggedIn()) {
      throw new Error('User is not logged in');
    }

    try {
      return liff.getDecodedIDToken() as IdToken;
    } catch (error) {
      console.error('Failed to get decoded ID token:', error);
      throw error;
    }
  }

  /**
   * decoded ID Token から sub (ユーザー固有ID) を取得
   * 失敗時は null を返し、コンソールに警告を出力
   */
  async getSub(): Promise<string | null> {
    try {
      const token = await this.getDecodedIdToken();
      return token?.sub || null;
    } catch (e) {
      console.warn('Could not retrieve sub from decoded ID token:', e);
      return null;
    }
  }

  /**
   * LINEユーザーID (userId/sub) を取得するためのユーティリティ
   * 取得優先度: LIFFコンテキスト > プロフィール > Decoded ID Token(sub)
   * いずれも取得できない場合は null
   */
  async getLineUserId(): Promise<string | null> {
    // 開発モードは固定値
    if (isDevelopmentMode()) {
      return 'mock-line-user-id';
    }

    if (!this.isLoggedIn()) return null;

    // 1. コンテキストから
    try {
      const ctx = this.getContext();
      if (ctx?.userId) return ctx.userId;
    } catch (_) {}

    // 2. プロフィールから
    try {
      const profile = await this.getProfile();
      if (profile?.userId) return profile.userId;
    } catch (_) {}

    // 3. decoded ID token (sub)
    try {
      const sub = await this.getSub();
      if (sub) return sub;
    } catch (_) {}

    return null;
  }

  getLiffData(): LiffData | null {
    try {
      const context = liff.getContext();
      const language = liff.getLanguage();
      
      if (!context) return null;

      return {
        language,
        context: {
          type: context.type,
          viewType: context.viewType as 'compact' | 'tall' | 'full' | 'frame' | 'full-flex',
          userId: context.userId,
          utouId: context.utouId,
          roomId: context.roomId,
          groupId: context.groupId,
          squareChatId: context.squareChatId,
        },
      };
    } catch (error) {
      console.error('Failed to get LIFF data:', error);
      return null;
    }
  }

  isInClient(): boolean {
    if (isDevelopmentMode()) {
      return false; // 開発モードでは外部ブラウザ扱い
    }
    return liff.isInClient();
  }

  openWindow(url: string, external = false): void {
    liff.openWindow({
      url,
      external,
    });
  }

  closeWindow(): void {
    liff.closeWindow();
  }

  async shareTargetPicker(messages: any[]): Promise<void> {
    if (!liff.isApiAvailable('shareTargetPicker')) {
      throw new Error('shareTargetPicker is not available');
    }

    try {
      await liff.shareTargetPicker(messages);
    } catch (error) {
      console.error('Failed to share:', error);
      throw error;
    }
  }

  getURLParameter(key: string): string | null {
    // 開発モードの場合はモック値を返す
    if (isDevelopmentMode()) {
      // テスト用のinvitationToken値を返す
      return 'invitationToken=test123';
    }

    try {
      // URLSearchParamsから`key`パラメータを取得
      const urlParams = new URLSearchParams(window.location.search);
      const value = urlParams.get(key);
      return value;
    } catch (error) {
      console.error('Failed to get URL Parameter:', error);
      return null;
    }
  }
}

export default LiffManager.getInstance();